import { useState } from 'react';
import { Box, Typography, Button, Paper, CircularProgress, Alert } from '@mui/material';
import PropTypes from 'prop-types';
import { AvailableThemes } from '../constants/ThemeType';

/**
 * Tela de primeira escolha de tema do usuário
 * Exibida como página intermediária antes do sistema carregar
 */
export default function ThemeSelectionModal({ onThemeSelected }) {
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Embaralhar temas para distribuição aleatória
  const shuffledThemes = [...AvailableThemes].sort(() => Math.random() - 0.5);
  
  const handleThemeClick = async (theme) => {
    if (loading) return; // Prevenir duplo clique
    
    setSelectedTheme(theme);
    setLoading(true);
    setError(null);
    
    try {
      await onThemeSelected(theme);
      // Sucesso - callback cuidará do resto
    } catch (err) {
      // Erro - mostrar mensagem e permitir nova tentativa
      setError(err.message || 'Erro ao salvar tema. Tente novamente.');
      setSelectedTheme(null);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 4,
        overflow: 'auto',
      }}
    >
      {/* Cabeçalho */}
      <Box sx={{ textAlign: 'center', mb: 4, maxWidth: '600px' }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: '#333',
            mb: 2,
          }}
        >
          Bem-vindo ao Agendário! 🎨
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#666',
          }}
        >
          Para começar, escolha o tipo do seu negócio. Isso personalizará a experiência do sistema para você.
        </Typography>
      </Box>

      {/* Mensagem de erro */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, maxWidth: '600px' }}>
          {error}
        </Alert>
      )}

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Salvando sua escolha...
          </Typography>
        </Box>
      )}

      {/* Nuvem de Temas */}
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: '900px',
          minHeight: '300px',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          p: 3,
          backgroundColor: '#f8f9fa',
          borderRadius: 2,
        }}
      >
        {shuffledThemes.map((theme) => {
          const isSelected = selectedTheme?.type === theme.type;
          const isOther = theme.type === 1; // ThemeType.DEFAULT

          return (
            <Button
              key={theme.type}
              variant={isSelected ? 'contained' : 'outlined'}
              onClick={() => handleThemeClick(theme)}
              disabled={loading}
              sx={{
                fontSize: '1rem',
                padding: '12px 24px',
                minWidth: '180px',
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: isSelected ? 600 : 500,
                backgroundColor: isSelected
                  ? '#1976d2'
                  : isOther
                  ? '#f5f5f5'
                  : 'white',
                borderColor: isSelected
                  ? '#1976d2'
                  : isOther
                  ? '#bdbdbd'
                  : '#e0e0e0',
                color: isSelected
                  ? 'white'
                  : isOther
                  ? '#757575'
                  : '#333',
                '&:hover': {
                  backgroundColor: isSelected ? '#1565c0' : '#f5f5f5',
                  borderColor: '#1976d2',
                },
                '&:disabled': {
                  opacity: loading ? 0.7 : (isSelected ? 1 : 0.5),
                },
              }}
            >
              {theme.name}
            </Button>
          );
        })}
      </Paper>

      {/* Rodapé */}
      <Box sx={{ textAlign: 'center', mt: 3, maxWidth: '500px' }}>
        <Typography
          variant="caption"
          sx={{
            color: '#999',
            fontSize: '0.875rem',
          }}
        >
          💡 Não se preocupe! Você pode alterar isso depois nas configurações.
        </Typography>
      </Box>
    </Box>
  );
}

ThemeSelectionModal.propTypes = {
  onThemeSelected: PropTypes.func.isRequired,
};
